import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Docs"` — the console's Documentation, drawn INSIDE the app shell exactly as
 * the prototype draws it (fidelity wave 2026-09-23): a 218 px article list on the left, the
 * article on the right, and "Was this page useful?" at the foot.
 *
 * The articles are `docs-content.ts`. The public developer reference (`apps/web-public`'s
 * `/docs/`) stays where it is; this is the in-console how-to the sidebar's "Documentation" opens.
 */
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { GLYPH } from "../../shell/glyph.js";
import {
  DOCS_ARTICLE,
  SUPPORT_EMAIL,
  docsNavigation,
  supportLine,
  type DocsArticleId,
  type DocsBlock,
} from "./docs-content.js";
import { TalkRatePanel } from "../billing/talk-rate-panel.js";
import "./docs.css";

function CodeSample({ block }: { readonly block: DocsBlock & { readonly code: string } }): ReactNode {
  usePortalTranslation("core");
  const [copied, setCopied] = useState(false);
  return (
    <div className="c-docs-code">
      <div className="c-docs-code-head">
        <span className="c-docs-code-label">{block.codeLabel ?? ""}</span>
        <button
          type="button"
          className="c-docs-code-copy"
          onClick={() => {
            void globalThis.navigator.clipboard?.writeText(block.code).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? portalText("core", "workspaces.copied_8e3df4") : portalText("core", "account_admin.copy_af74f7")}
        </button>
      </div>
      <pre className="c-docs-code-body">{block.code}</pre>
    </div>
  );
}

export interface DocsScreenProps {
  readonly docsArticleId: DocsArticleId;
}

export function DocsScreen({ docsArticleId }: DocsScreenProps): ReactNode {
  usePortalTranslation("core");
  const article = DOCS_ARTICLE[docsArticleId];
  const gateway = useGateway();
  const [useful, setUseful] = useState<boolean | null>(null);
  const help = supportLine();

  return (
    <div className="c-docs">
      <aside className="c-docs-side" aria-label={portalText("core", "docs.documentation_9e9cf3")}>
        <p className="c-docs-side-label">{portalText("core", "docs.documentation_9e9cf3")}</p>
        <div className="c-docs-side-list">
          {docsNavigation().map(([id, label]) => (
            <Link
              key={id}
              to="/docs/$docsArticleId"
              params={{ docsArticleId: id }}
              className="c-docs-side-link"
              aria-current={id === docsArticleId ? "page" : undefined}
            >
              {label}
            </Link>
          ))}
          {/* The public API reference (customer-portal review item 188), on the public site. */}
          <a href={gateway.apiReferenceUrl()} className="c-docs-side-link" target="_blank" rel="noopener">{portalText("core", "docs.api_reference_335927")}</a>
        </div>
        {/* AMENDED 2026-10-13: support@wetalk.io receives mail, so the rail links to it. */}
        <p className="c-docs-side-help">
          {help.before}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          {help.after}
        </p>
      </aside>

      <section className="c-docs-article">
        <p className="c-docs-kicker">{article.kicker}</p>
        <h1 className="c-docs-title">{article.title}</h1>
        <p className="c-docs-lede">{article.lede}</p>
        {/* plan-017 WAPP-B (OQ-1): the Billing article shows the organization's own rates, read through the hook. */}
        {docsArticleId === "billing" ? <TalkRatePanel variant="dashboard" /> : null}
        {article.block.map((block) => (
          <div className="c-docs-block" key={block.heading}>
            <h2 className="c-docs-heading">{block.heading}</h2>
            {block.paragraph.map((one) => (
              <p className="c-docs-paragraph" key={one}>
                {one}
              </p>
            ))}
            {block.bullet === undefined ? null : (
              <ul className="c-docs-bullets">
                {block.bullet.map((one) => (
                  <li key={one}>{one}</li>
                ))}
              </ul>
            )}
            {block.code === undefined ? null : <CodeSample block={{ ...block, code: block.code }} />}
          </div>
        ))}

        <div className="c-docs-useful">
          <span className="c-docs-useful-ask">
            {useful === null ? portalText("core", "docs.was_this_page_useful_94c1d3") : portalText("core", "docs.thank_you_that_helps_us_decide_what_to_write_next_5ba66a")}
          </span>
          <span className="c-docs-useful-actions">
            <button
              type="button"
              className="btn btn-secondary"
              aria-pressed={useful === true}
              onClick={() => {
                setUseful(true);
              }}
            >
              <Icon glyph={GLYPH.thumbsUp} size="md" />{portalText("core", "account_admin.yes_5397e0")}</button>
            <button
              type="button"
              className="btn btn-secondary"
              aria-pressed={useful === false}
              onClick={() => {
                setUseful(false);
              }}
            >
              <Icon glyph={GLYPH.thumbsDown} size="md" />{portalText("core", "docs.no_816c52")}</button>
          </span>
        </div>
      </section>
    </div>
  );
}
